import React, { useState, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { EducationLevel, SearchFilters } from '../types';
import filterOptions from '../data/filterOptions.json';
import skillsAndLanguages from '../data/skillsAndLanguages.json';
import topSkills from '../data/topSkills.json';
import topStemSkills from '../data/topStemSkills.json';
import stemSkillsData from '../data/stemSkills.json';
import topFinanceSkills from '../data/topFinanceSkills.json';
import financeSkillsData from '../data/financeSkills.json';
import topDsmlSkills from '../data/topDsmlSkills.json';
import dsmlSkillsData from '../data/dsmlSkills.json';
import naturalLanguagesData from '../data/naturalLanguages.json';
import { Filter, X, ChevronDown, ChevronUp, Search, Eye, Heart, Ban } from 'lucide-react';
import { Switch } from './ui/switch';
import { getDomainConfig, getFilterVisibility, DEFAULT_LOCATION_OPTIONS } from '../config/appConfig';
import { trackEvent } from '../utils/mixpanel';
import './FilterSection.css';

const ExpertiseChip: React.FC<{
  label: string;
  isSelected: boolean;
  onClick: () => void;
}> = ({ label, isSelected, onClick }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number } | null>(null);

  const handleMouseEnter = useCallback(() => {
    const el = btnRef.current;
    if (el && el.scrollWidth > el.clientWidth) {
      const rect = el.getBoundingClientRect();
      setTooltip({ x: rect.left + rect.width / 2, y: rect.top });
    }
  }, []);

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  return (
    <span className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`w-full h-7 px-2.5 text-[12px] font-semibold rounded-lg transition-all truncate ${
          isSelected
            ? 'bg-zinc-900 dark:bg-slate-100 text-white dark:text-slate-900'
            : 'bg-zinc-100 dark:bg-slate-800 text-zinc-700 dark:text-slate-300 hover:bg-zinc-200 dark:hover:bg-slate-700'
        }`}
      >
        {label}
      </button>
      {tooltip && createPortal(
        <span
          className="fixed px-2 py-1 text-[11px] font-medium text-white bg-zinc-900 dark:bg-slate-700 rounded-md whitespace-nowrap pointer-events-none z-[9999] shadow-lg"
          style={{ left: tooltip.x, top: tooltip.y - 8, transform: 'translate(-50%, -100%)' }}
        >
          {label}
          <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-zinc-900 dark:border-t-slate-700" />
        </span>,
        document.body
      )}
    </span>
  );
};

const MAX_RECENT_SKILLS = 7;

function recentSkillsKey(domain: string, field?: string): string {
  const base = `search_v3_recent_${domain.toLowerCase()}_skills`;
  return field ? `${base}_${field.toLowerCase()}` : base;
}

function getRecentSkillsForDomain(domain: string, field?: string): string[] {
  try {
    const raw = localStorage.getItem(recentSkillsKey(domain, field));
    return (raw ? JSON.parse(raw) : []).slice(0, MAX_RECENT_SKILLS);
  } catch {
    return [];
  }
}

function addToRecentSkillsForDomain(skill: string, domain: string, field?: string): string[] {
  const prev = getRecentSkillsForDomain(domain, field);
  const next = [skill, ...prev.filter((s) => s !== skill)].slice(0, MAX_RECENT_SKILLS);
  localStorage.setItem(recentSkillsKey(domain, field), JSON.stringify(next));
  return next;
}

const MAX_RECENT_NATURAL_LANGS = 7;

function recentNaturalLangsKey(domain: string): string {
  return `search_v3_recent_${domain.toLowerCase()}_natural_langs`;
}

function getRecentNaturalLangsForDomain(domain: string): string[] {
  try {
    const raw = localStorage.getItem(recentNaturalLangsKey(domain));
    return (raw ? JSON.parse(raw) : []).slice(0, MAX_RECENT_NATURAL_LANGS);
  } catch {
    return [];
  }
}

function addToRecentNaturalLangsForDomain(lang: string, domain: string): string[] {
  const prev = getRecentNaturalLangsForDomain(domain);
  const next = [lang, ...prev.filter((l) => l !== lang)].slice(0, MAX_RECENT_NATURAL_LANGS);
  localStorage.setItem(recentNaturalLangsKey(domain), JSON.stringify(next));
  return next;
}

interface FilterSectionProps {
  filters: SearchFilters;
  onFilterChange: (filters: SearchFilters) => void;
  onApplyFilters?: () => void;
  onInteraction?: () => void;
  /** When true (e.g. in mobile drawer), use full height of container instead of max-h; avoids ~50% viewport cap */
  fillHeight?: boolean;
  configReady?: boolean;
}

const FilterSection: React.FC<FilterSectionProps> = ({ filters, onFilterChange, onApplyFilters, onInteraction, fillHeight = false, configReady = false }) => {
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(
    new Set(['typeOfWork', 'field', 'domainFilter', 'skills', 'exclude', 'countries'])
  );
  const [skillsInput, setSkillsInput] = useState('');
  const [naturalLangInput, setNaturalLangInput] = useState('');
  const [programmingLangInput, setProgrammingLangInput] = useState('');
  const [schoolInput, setSchoolInput] = useState('');
  const [companiesInput, setCompaniesInput] = useState('');
  const [activeSlider, setActiveSlider] = useState<'min' | 'max' | null>(null);
  const [recentSkillsDirty, setRecentSkillsDirty] = useState(0);
  const [recentNaturalLangsDirty, setRecentNaturalLangsDirty] = useState(0);

  const domain = filters.domain ?? 'SWE';
  const filterVis = getFilterVisibility(domain);
  const stemField = domain === 'STEM' ? filters.field : undefined;
  const recentSkills = useMemo(
    () => getRecentSkillsForDomain(domain, stemField),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [domain, stemField, recentSkillsDirty]
  );
  const recentNaturalLangs = useMemo(
    () => getRecentNaturalLangsForDomain(domain),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [domain, recentNaturalLangsDirty]
  );

  // Skills data mapping per domain (new domains fall back to config skillsConfig, then SWE data)
  const stemTopSkills = (topStemSkills as Record<string, string[]>)[filters.field || ''] ?? (topStemSkills as Record<string, string[]>)['all'];
  const SKILLS_BY_DOMAIN: Record<string, { all: string[]; top: string[] }> = useMemo(() => ({
    SWE: { all: skillsAndLanguages.sweSkills as string[], top: topSkills.swe as string[] },
    STEM: { all: stemSkillsData as string[], top: stemTopSkills },
    Finance: { all: financeSkillsData as string[], top: topFinanceSkills as string[] },
    DSML: { all: dsmlSkillsData as string[], top: topDsmlSkills as string[] },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [stemTopSkills]);

  const configSkills = useMemo(() => {
    const sc = getDomainConfig(domain).skillsConfig as Record<string, { top: string[]; all: string[] }> | undefined;
    if (!sc) return null;
    const entry = sc[filters.field || ''] ?? sc['_default'];
    return entry ?? null;
  }, [domain, filters.field, configReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const skillsForDomain = configSkills ?? SKILLS_BY_DOMAIN[domain] ?? null;
  const allSkillsForDomain = useMemo(() => skillsForDomain?.all ?? [], [skillsForDomain]);
  const topSkillsForDomain = useMemo(() => skillsForDomain?.top ?? [], [skillsForDomain]);

  const allNaturalLangs: string[] = (naturalLanguagesData.all as Record<string, string[]>)[domain] ?? (naturalLanguagesData.all as Record<string, string[]>)['SWE'];
  const topNaturalLangsForDomain: string[] = (naturalLanguagesData.top as Record<string, string[]>)[domain] ?? (naturalLanguagesData.top as Record<string, string[]>)['SWE'];

  const locationOptions = getDomainConfig(domain).locationOptions ?? DEFAULT_LOCATION_OPTIONS;

  // Expertise options from config (auto-hides when empty)
  const expertiseOptions: string[] = getDomainConfig(domain).expertiseOptions ?? [];
  const fieldOptions: string[] = getDomainConfig(domain).fieldOptions ?? [];
  const domainFilterOptions: string[] = getDomainConfig(domain).domainFilterOptions ?? [];

  const toggleFilter = (filterKey: string) => {
    const newExpanded = new Set(expandedFilters);
    if (newExpanded.has(filterKey)) {
      newExpanded.delete(filterKey);
    } else {
      newExpanded.add(filterKey);
    }
    setExpandedFilters(newExpanded);
    // Trigger first interaction
    if (onInteraction) {
      onInteraction();
    }
  };

  const updateFilter = <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
    if (onInteraction) {
      onInteraction();
    }
    const trackValue = key === 'skills'
      ? (value as any[])?.map((s: any) => s.name)
      : value;
    trackEvent('Filter Changed', { filterType: key, value: trackValue, domain });
    onFilterChange({ ...filters, [key]: value });
  };

  const addArrayItem = (key: keyof SearchFilters, item: string) => {
    const current = (filters[key] as string[]) || [];
    if (item && !current.includes(item)) {
      updateFilter(key, [...current, item] as any);
    }
  };

  const removeArrayItem = (key: keyof SearchFilters, item: string) => {
    const current = (filters[key] as string[]) || [];
    updateFilter(key, current.filter(i => i !== item) as any);
  };

  // Skills to show as pills: SWE or STEM by domain; recent + top 15 when empty, search when typing
  const skillsPills = useMemo(() => {
    const input = (skillsInput || '').trim().toLowerCase();
    const selectedSkillNames = (filters.skills || []).map(s => s.name).filter(Boolean);

    if (!input) {
      return [...recentSkills, ...topSkillsForDomain.filter((s) => !recentSkills.includes(s))];
    }

    const matched = allSkillsForDomain.filter((skill: string) =>
      skill.toLowerCase().includes(input)
    );
    const selected = matched.filter((s: string) => selectedSkillNames.includes(s));
    const unselected = matched.filter((s: string) => !selectedSkillNames.includes(s));
    return [...selected, ...unselected].slice(0, 24);
  }, [skillsInput, filters.skills, recentSkills, allSkillsForDomain, topSkillsForDomain]);

  // Natural languages pills: recent (max 7) + top 15 for domain when empty, search suggestions when user types (like skills)
  const naturalLangPills = useMemo(() => {
    const input = (naturalLangInput || '').trim().toLowerCase();
    const selected = filters.naturalLanguages || [];

    if (!input) {
      return [...recentNaturalLangs, ...topNaturalLangsForDomain.filter((l) => !recentNaturalLangs.includes(l))];
    }

    const matched = allNaturalLangs.filter((lang) =>
      lang.toLowerCase().includes(input)
    );
    const selectedInMatched = matched.filter((l) => selected.includes(l));
    const unselectedInMatched = matched.filter((l) => !selected.includes(l));
    return [...selectedInMatched, ...unselectedInMatched].slice(0, 24);
  }, [naturalLangInput, filters.naturalLanguages, recentNaturalLangs, topNaturalLangsForDomain, allNaturalLangs]);

  const filteredProgrammingLangs = useMemo(() => {
    const selected = filters.programmingLanguages || [];
    const input = programmingLangInput.toLowerCase();
    const allLangs = skillsAndLanguages.programmingLanguages.filter(lang => 
      !input || lang.toLowerCase().includes(input)
    );
    
    const selectedLangs = allLangs.filter(lang => selected.includes(lang));
    const unselectedLangs = allLangs.filter(lang => !selected.includes(lang));
    return [...selectedLangs, ...unselectedLangs];
  }, [programmingLangInput, filters.programmingLanguages]);

  // No need for filteredCountries - we only have 4 fixed options

  const filteredCompanies = useMemo(() => {
    const selected = filters.companies || [];
    const input = companiesInput.toLowerCase();
    const allCompanies = filterOptions.companies.filter(company => 
      !input || company.toLowerCase().includes(input)
    );
    
    const selectedCompanies = allCompanies.filter(company => selected.includes(company));
    const unselectedCompanies = allCompanies.filter(company => !selected.includes(company));
    return [...selectedCompanies, ...unselectedCompanies];
  }, [companiesInput, filters.companies]);

  const filteredSchools = useMemo(() => {
    const input = schoolInput.toLowerCase();
    return filterOptions.universities.filter(school =>
      !input || school.toLowerCase().includes(input)
    );
  }, [schoolInput]);

  const renderFilterHeader = (
    value: any,
    filterKey: keyof SearchFilters,
    label: string,
    isExpanded: boolean
  ) => {
    const isActive = value !== undefined && value !== null && value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true);

    const handleClearFilter = (e: React.MouseEvent) => {
      e.stopPropagation();
      trackEvent('Filter Cleared', { filterType: filterKey, domain });
      const newFilters: SearchFilters = { ...filters };
      if (Array.isArray(filters[filterKey])) {
        (newFilters as any)[filterKey] = undefined;
      } else if (typeof filters[filterKey] === 'object' && filterKey !== 'yearOfExperience') {
        (newFilters as any)[filterKey] = undefined;
      } else if (filterKey === 'yearOfExperience') {
        newFilters.yearOfExperience = undefined;
      } else {
        (newFilters as any)[filterKey] = undefined;
      }
      onFilterChange(newFilters);
    };

    return (
      <div
        className="flex items-center justify-between w-full mb-2.5 cursor-pointer"
        onClick={() => toggleFilter(filterKey)}
      >
        <h4 className="text-[12px] font-bold tracking-[0.08em] text-zinc-900 dark:text-slate-100 uppercase">
          {label}
        </h4>
        <div className="flex items-center gap-1.5">
          {isActive && (
            <button
              type="button"
              onClick={handleClearFilter}
              className="text-zinc-400 hover:text-red-500 transition-colors"
              title="Clear filter"
            >
              <X className="w-3 h-3" />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="w-3.5 h-3.5 text-zinc-400" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />
          )}
        </div>
      </div>
    );
  };

  const handleClear = () => {
    trackEvent('Filters Cleared', { domain });
    onFilterChange({
      domain: filters.domain,
      page: 1,
      pageSize: 18,
    });
  };

  // Count active filters
  const getActiveFilterCount = (): number => {
    let count = 0;
    if (filters.searchQuery && filters.searchQuery.trim().length > 0) count++;
    if (filters.typeOfWork && filters.typeOfWork.trim().length > 0) count++;
    if (filters.field && filters.field.trim().length > 0) count++;
    if (filters.domainFilter && filters.domainFilter.trim().length > 0) count++;
    if (filters.countries && filters.countries.length > 0) count++;
    if (filters.companies && filters.companies.length > 0) count++;
    if (filters.yearOfExperience && (filters.yearOfExperience.min !== undefined || filters.yearOfExperience.max !== undefined)) count++;
    if (filters.education && filters.education.length > 0) count++;
    if (filters.school && filters.school.trim().length > 0) count++;
    if (filters.certificates) count++;
    if (filters.skills && filters.skills.length > 0) count++;
    if (filters.researchPapersCount !== undefined) count++;
    if (filters.naturalLanguages && filters.naturalLanguages.length > 0) count++;
    if (filters.programmingLanguages && filters.programmingLanguages.length > 0) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();
  const hasActiveFilters = activeFilterCount > 0;

  // Build active filters summary
  const getActiveFiltersSummary = () => {
    const parts: string[] = [];
    if (filters.searchQuery && filters.searchQuery.trim().length > 0) {
      parts.push(`Search: "${filters.searchQuery}"`);
    }
    if (filters.yearOfExperience && (filters.yearOfExperience.min !== undefined || filters.yearOfExperience.max !== undefined)) {
      const min = filters.yearOfExperience.min;
      const max = filters.yearOfExperience.max;
      if (min !== undefined && max !== undefined) {
        parts.push(`Experience: ${min}-${max} yrs`);
      } else if (min !== undefined) {
        parts.push(`Experience: ${min}+ yrs`);
      } else if (max !== undefined) {
        parts.push(`Experience: up to ${max} yrs`);
      }
    }
    if (filters.countries && filters.countries.length > 0) {
      parts.push(`Location: ${filters.countries.slice(0, 2).join(', ')}${filters.countries.length > 2 ? '...' : ''}`);
    }
    if (filters.companies && filters.companies.length > 0) {
      parts.push(`Companies: ${filters.companies.slice(0, 2).join(', ')}${filters.companies.length > 2 ? '...' : ''}`);
    }
    if (filters.skills && filters.skills.length > 0) {
      const skillNames = filters.skills.map(s => s.name || 'Unknown').filter(Boolean);
      if (skillNames.length <= 2) {
        parts.push(`Skills: ${skillNames.join(', ')}`);
      } else {
        parts.push(`Skills: ${skillNames.slice(0, 2).join(', ')} and ${skillNames.length - 2} more`);
      }
    }
    if (filters.naturalLanguages && filters.naturalLanguages.length > 0) {
      parts.push(`Languages: ${filters.naturalLanguages.slice(0, 2).join(', ')}${filters.naturalLanguages.length > 2 ? '...' : ''}`);
    }
    if (filters.programmingLanguages && filters.programmingLanguages.length > 0) {
      parts.push(`Programming: ${filters.programmingLanguages.slice(0, 2).join(', ')}${filters.programmingLanguages.length > 2 ? '...' : ''}`);
    }
    if (filters.education && filters.education.length > 0) {
      parts.push(`Education: ${filters.education.slice(0, 2).join(', ')}${filters.education.length > 2 ? '...' : ''}`);
    }
    if (filters.school && filters.school.trim().length > 0) {
      parts.push(`School: ${filters.school}`);
    }
    if (filters.certificates) {
      parts.push(`Certificates: ${filters.certificates}`);
    }
    if (filters.researchPapersCount !== undefined) {
      parts.push(`Research Papers: ${filters.researchPapersCount}+`);
    }
    if (filters.typeOfWork && filters.typeOfWork.trim().length > 0) {
      parts.push(`Expertise: ${filters.typeOfWork}`);
    }
    if (filters.field && filters.field.trim().length > 0) {
      parts.push(`Field: ${filters.field}`);
    }
    if (filters.domainFilter && filters.domainFilter.trim().length > 0) {
      parts.push(`Domain: ${filters.domainFilter}`);
    }
    return parts.join(' · ');
  };

  // Get YOE range values (default 1–15 when not set; experience starts from 1+)
  const minYoe = filters.yearOfExperience?.min ?? 1;
  const maxYoe = filters.yearOfExperience?.max ?? 15;
  const maxYoeValue = 15; // Maximum years of experience
  const minYoeValue = 1;  // Minimum years of experience
  const yoeRange = maxYoeValue - minYoeValue; // 14 — track maps 1→0%, 15→100%
  const yoeToPercent = (v: number) => ((v - minYoeValue) / yoeRange) * 100;

  return (
    <div
      className={
        fillHeight
          ? 'min-h-0 flex flex-col bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-zinc-200 dark:border-slate-800 overscroll-contain [touch-action:manipulation] h-full'
          : 'flex flex-col bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-zinc-200 dark:border-slate-800 overscroll-contain [touch-action:manipulation] h-full'
      }
    >
      <div className="px-4 py-3 bg-white dark:bg-slate-900 z-20 border-b border-zinc-100 dark:border-slate-800 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-zinc-700 dark:text-slate-300" />
            <h3 className="text-[12px] font-bold tracking-[0.08em] text-zinc-900 dark:text-slate-100 uppercase">
              Filters
            </h3>
          </div>
          {hasActiveFilters && (
            <button 
              onClick={handleClear} 
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>

        {hasActiveFilters && (
          <div className="p-3 bg-zinc-50 dark:bg-slate-800/50 rounded-lg border border-zinc-200 dark:border-slate-700">
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-xs font-semibold text-zinc-700 dark:text-slate-300">
                Active filters ({activeFilterCount})
              </p>
            </div>
            <p className="text-xs text-zinc-600 dark:text-slate-400 leading-relaxed">
              {getActiveFiltersSummary()}
            </p>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden filter-content px-4 py-3 space-y-4">

        {/* Expertise Filter (typeOfWork) - FIRST; hidden when config has no options for this domain */}
        {filterVis.expertise && expertiseOptions.length > 0 && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.typeOfWork,
            'typeOfWork',
            'Expertise',
            expandedFilters.has('typeOfWork')
          )}

          {expandedFilters.has('typeOfWork') && (
            <div className="grid grid-cols-2 gap-2">
              {expertiseOptions.map((expertise) => (
                <ExpertiseChip
                  key={expertise}
                  label={expertise}
                  isSelected={filters.typeOfWork === expertise}
                  onClick={() => updateFilter('typeOfWork', filters.typeOfWork === expertise ? undefined : expertise)}
                />
              ))}
            </div>
          )}
        </div>
        )}

        {/* Field Filter (STEM) — single-select chips, same UI pattern as Expertise */}
        {filterVis.field && fieldOptions.length > 0 && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.field,
            'field',
            'Field',
            expandedFilters.has('field')
          )}

          {expandedFilters.has('field') && (
            <div className="grid grid-cols-2 gap-2">
              {fieldOptions.map((opt) => (
                <ExpertiseChip
                  key={opt}
                  label={opt}
                  isSelected={filters.field === opt}
                  onClick={() => {
                    const newField = filters.field === opt ? undefined : opt;
                    if (newField !== filters.field) {
                      onFilterChange({ ...filters, field: newField, skills: undefined });
                      trackEvent('Filter Changed', { filterType: 'field', value: newField, domain });
                      if (onInteraction) onInteraction();
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
        )}

        {/* Domain Filter (Finance) — single-select chips, same UI pattern as Expertise */}
        {filterVis.domainFilter && domainFilterOptions.length > 0 && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.domainFilter,
            'domainFilter',
            'Domain',
            expandedFilters.has('domainFilter')
          )}

          {expandedFilters.has('domainFilter') && (
            <div className="grid grid-cols-2 gap-2">
              {domainFilterOptions.map((opt) => (
                <ExpertiseChip
                  key={opt}
                  label={opt}
                  isSelected={filters.domainFilter === opt}
                  onClick={() => updateFilter('domainFilter', filters.domainFilter === opt ? undefined : opt)}
                />
              ))}
            </div>
          )}
        </div>
        )}

        {/* Skills Filter */}
        {filterVis.skills && skillsForDomain !== null && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.skills,
            'skills',
            'Skills',
            expandedFilters.has('skills')
          )}

          {expandedFilters.has('skills') && (
            <div className="space-y-2.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search skills…"
                  value={skillsInput}
                  onChange={(e) => setSkillsInput(e.target.value)}
                  className="w-full h-9 pl-8 pr-3 text-[13px] bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-700 rounded-lg placeholder-zinc-400 dark:placeholder-slate-500 text-zinc-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-slate-400 focus:border-transparent"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {/* Show selected skills that aren't in the pills list */}
                {(filters.skills || [])
                  .filter((s) => s.name && !skillsPills.includes(s.name))
                  .map((s) => (
                    <button
                      key={`selected-${s.name}`}
                      type="button"
                      onClick={() => {
                        const current = filters.skills || [];
                        const updated = current.filter((x) => (x.name || '') !== s.name);
                        updateFilter('skills', updated.length > 0 ? updated : undefined);
                      }}
                      className="h-6 px-2 text-[11.5px] font-semibold rounded-md transition-all bg-zinc-900 dark:bg-slate-100 text-white dark:text-slate-900"
                    >
                      {s.name}
                    </button>
                  ))}
                {/* All pills with toggle */}
                {skillsPills.map((skill) => {
                  const isSelected = (filters.skills || []).some((s) => s.name === skill);
                  return (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          const current = filters.skills || [];
                          const updated = current.filter((x) => (x.name || '') !== skill);
                          updateFilter('skills', updated.length > 0 ? updated : undefined);
                        } else {
                          const current = filters.skills || [];
                          updateFilter('skills', [...current, { name: skill }]);
                          addToRecentSkillsForDomain(skill, domain, stemField);
                          setRecentSkillsDirty((v) => v + 1);
                          setSkillsInput('');
                        }
                      }}
                      className={`h-6 px-2 text-[11.5px] font-semibold rounded-md transition-all ${
                        isSelected
                          ? 'bg-zinc-900 dark:bg-slate-100 text-white dark:text-slate-900'
                          : 'bg-zinc-100 dark:bg-slate-800 text-zinc-600 dark:text-slate-400 hover:bg-zinc-200 dark:hover:bg-slate-700 hover:text-zinc-900 dark:hover:text-slate-200'
                      }`}
                    >
                      {skill}
                    </button>
                  );
                })}
              </div>
              {skillsInput.trim() && skillsPills.length === 0 && (
                <p className="text-[11px] text-zinc-500 dark:text-slate-400">No skills matching &quot;{skillsInput}&quot;</p>
              )}
            </div>
          )}
        </div>
        )}

        {/* Exclude Filter */}
        {filterVis.exclude && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          <button
            type="button"
            onClick={() => toggleFilter('exclude')}
            className="flex items-center justify-between w-full py-2 text-left"
          >
            <span className="text-[13px] font-semibold text-zinc-900 dark:text-slate-100">
              EXCLUDE
            </span>
            {expandedFilters.has('exclude') ? (
              <ChevronUp className="w-4 h-4 text-zinc-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-zinc-400" />
            )}
          </button>

          {expandedFilters.has('exclude') && (
            <div className="space-y-3 pt-1">
              {/* Exclude Viewed */}
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-zinc-500 dark:text-slate-400" />
                  <span className="text-[12.5px] text-zinc-700 dark:text-slate-300">Viewed</span>
                </div>
                <Switch
                  checked={!!filters.excludeViewed}
                  onCheckedChange={(checked) => updateFilter('excludeViewed', checked || undefined)}
                />
              </label>

              {/* Exclude Saved */}
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-zinc-500 dark:text-slate-400" />
                  <span className="text-[12.5px] text-zinc-700 dark:text-slate-300">Saved</span>
                </div>
                <Switch
                  checked={!!filters.excludeSaved}
                  onCheckedChange={(checked) => updateFilter('excludeSaved', checked || undefined)}
                />
              </label>

              {/* Exclude Discarded */}
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-2">
                  <Ban className="w-4 h-4 text-zinc-500 dark:text-slate-400" />
                  <span className="text-[12.5px] text-zinc-700 dark:text-slate-300">Discarded</span>
                </div>
                <Switch
                  checked={!!filters.excludeDiscarded}
                  onCheckedChange={(checked) => updateFilter('excludeDiscarded', checked || undefined)}
                />
              </label>
            </div>
          )}
        </div>
        )}

        {/* Location Filter */}
        {filterVis.location && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.countries,
            'countries',
            'Location',
            expandedFilters.has('countries')
          )}

          {expandedFilters.has('countries') && (
            <div className="flex flex-wrap gap-2">
              {locationOptions.map((location) => {
                const isSelected = (filters.countries || []).includes(location);
                return (
                  <button
                    key={location}
                    type="button"
                    onClick={() => {
                      const current = filters.countries || [];
                      if (isSelected) {
                        const updated = current.filter((c) => c !== location);
                        updateFilter('countries', updated.length > 0 ? updated : undefined);
                      } else {
                        updateFilter('countries', [...current, location]);
                      }
                    }}
                    className={`h-6 px-2 text-[11.5px] font-semibold rounded-md transition-all ${
                      isSelected
                        ? 'bg-zinc-900 dark:bg-slate-100 text-white dark:text-slate-900'
                        : 'bg-zinc-100 dark:bg-slate-800 text-zinc-600 dark:text-slate-400 hover:bg-zinc-200 dark:hover:bg-slate-700 hover:text-zinc-900 dark:hover:text-slate-200'
                    }`}
                  >
                    {location}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* Experience Range Filter */}
        {filterVis.experience && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.yearOfExperience,
            'yearOfExperience',
            'Experience',
            expandedFilters.has('yearOfExperience')
          )}

          {expandedFilters.has('yearOfExperience') && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <span className="text-[11px] text-zinc-500 dark:text-slate-400">
                  Range
                </span>
                <span className="text-[11px] font-bold text-zinc-900 dark:text-slate-100 bg-zinc-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                  {minYoe}-{maxYoe === maxYoeValue ? `${maxYoe}+` : maxYoe} yrs
                </span>
              </div>
              <div className="relative pt-2 pb-3 px-2">
                <div className="relative h-1.5 bg-zinc-200 dark:bg-slate-800 rounded-full">
                  <div 
                    className="absolute h-full bg-zinc-900 dark:bg-slate-100 rounded-full" 
                    style={{
                      left: `${yoeToPercent(minYoe)}%`,
                      right: `${100 - yoeToPercent(maxYoe)}%`
                    }}
                  ></div>
                  {/* Min slider - handles left thumb, covers left half (track min is 1, not 0) */}
                  <input 
                    type="range" 
                    min={minYoeValue} 
                    max={maxYoeValue} 
                    value={minYoe} 
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (val <= maxYoe) {
                        updateFilter('yearOfExperience', {
                          ...filters.yearOfExperience,
                          min: val,
                          max: filters.yearOfExperience?.max
                        });
                      }
                    }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setActiveSlider('min');
                    }}
                    onPointerUp={(e) => {
                      e.stopPropagation();
                      setActiveSlider(null);
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      setActiveSlider('min');
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      setActiveSlider(null);
                    }}
                    className="absolute w-full h-full opacity-0 cursor-pointer" 
                    style={{ 
                      zIndex: activeSlider === 'min' ? 30 : 20,
                      pointerEvents: activeSlider === 'max' ? 'none' : 'auto',
                      clipPath: `inset(0 ${100 - yoeToPercent((minYoe + maxYoe) / 2)}% 0 0)`
                    }}
                  />
                  {/* Max slider - handles right thumb, covers right half */}
                  <input 
                    type="range" 
                    min={minYoe} 
                    max={maxYoeValue} 
                    value={maxYoe} 
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (val >= minYoe) {
                        updateFilter('yearOfExperience', {
                          ...filters.yearOfExperience,
                          min: filters.yearOfExperience?.min,
                          max: val === maxYoeValue ? undefined : val
                        });
                      }
                    }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setActiveSlider('max');
                    }}
                    onPointerUp={(e) => {
                      e.stopPropagation();
                      setActiveSlider(null);
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      setActiveSlider('max');
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      setActiveSlider(null);
                    }}
                    className="absolute w-full h-full opacity-0 cursor-pointer" 
                    style={{ 
                      zIndex: activeSlider === 'max' ? 30 : 24,
                      pointerEvents: activeSlider === 'min' ? 'none' : 'auto',
                      clipPath: `inset(0 0 0 ${yoeToPercent((minYoe + maxYoe) / 2)}%)`
                    }}
                  />
                  {/* Thumbs */}
                  <div 
                    className="absolute w-4 h-4 bg-white border-2 border-zinc-900 dark:border-slate-100 rounded-full shadow top-1/2 -translate-y-1/2 -ml-2 pointer-events-none z-10" 
                    style={{
                      left: `${yoeToPercent(minYoe)}%`
                    }}
                  ></div>
                  <div 
                    className="absolute w-4 h-4 bg-white border-2 border-zinc-900 dark:border-slate-100 rounded-full shadow top-1/2 -translate-y-1/2 -ml-2 pointer-events-none z-10" 
                    style={{
                      left: `${yoeToPercent(maxYoe)}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Hourly Rate Filter */}
        {filterVis.rate && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.hourlyRate,
            'hourlyRate' as any,
            'Hourly Rate',
            expandedFilters.has('hourlyRate')
          )}
          {expandedFilters.has('hourlyRate') && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] text-zinc-500 dark:text-slate-400 mb-1">Min $/hr</label>
                <input
                  type="number"
                  min={0}
                  value={filters.hourlyRate?.min ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? undefined : Number(e.target.value);
                    const max = filters.hourlyRate?.max;
                    if (v === undefined && max === undefined) {
                      updateFilter('hourlyRate' as any, undefined);
                    } else {
                      updateFilter('hourlyRate' as any, { min: v, max });
                    }
                  }}
                  className="w-full h-9 px-2 text-[13px] bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-700 rounded-lg text-zinc-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-slate-400"
                />
              </div>
              <div>
                <label className="block text-[11px] text-zinc-500 dark:text-slate-400 mb-1">Max $/hr</label>
                <input
                  type="number"
                  min={0}
                  value={filters.hourlyRate?.max ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? undefined : Number(e.target.value);
                    const min = filters.hourlyRate?.min;
                    if (v === undefined && min === undefined) {
                      updateFilter('hourlyRate' as any, undefined);
                    } else {
                      updateFilter('hourlyRate' as any, { min, max: v });
                    }
                  }}
                  className="w-full h-9 px-2 text-[13px] bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-700 rounded-lg text-zinc-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-slate-400"
                />
              </div>
            </div>
          )}
        </div>
        )}

        {/* Companies Filter */}
        {filterVis.companies && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.companies,
            'companies',
            'Companies',
            expandedFilters.has('companies')
          )}

          {expandedFilters.has('companies') && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={companiesInput}
                  onChange={(e) => setCompaniesInput(e.target.value)}
                  className="w-full h-9 pl-8 pr-3 text-[13px] bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-700 rounded-lg placeholder-zinc-400 dark:placeholder-slate-500 text-zinc-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-slate-400 focus:border-transparent"
                />
              </div>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {filteredCompanies.map((company) => (
                  <label key={company} className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                      (filters.companies || []).includes(company) 
                        ? 'bg-zinc-900 dark:bg-slate-100 border-zinc-900 dark:border-slate-100' 
                        : 'bg-white dark:bg-slate-800 border-zinc-300 dark:border-slate-700 group-hover:border-zinc-400'
                    }`}>
                      {(filters.companies || []).includes(company) && (
                        <svg className="w-3 h-3 text-white dark:text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={(filters.companies || []).includes(company)}
                      onChange={(e) => {
                        const current = filters.companies || [];
                        const updated = e.target.checked
                          ? [...current, company]
                          : current.filter(c => c !== company);
                        updateFilter('companies', updated.length > 0 ? updated : undefined);
                      }}
                      className="hidden"
                    />
                    <span className="text-[13px] text-zinc-600 dark:text-slate-400 group-hover:text-zinc-900 dark:group-hover:text-slate-100 transition-colors">
                      {company}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Education Filter */}
        {filterVis.education && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.education,
            'education',
            'Education',
            expandedFilters.has('education')
          )}

          {expandedFilters.has('education') && (
            <div className="space-y-2">
              {Object.values(EducationLevel).map((level) => (
                <label key={level} className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                    (filters.education || []).includes(level) 
                      ? 'bg-zinc-900 dark:bg-slate-100 border-zinc-900 dark:border-slate-100' 
                      : 'bg-white dark:bg-slate-800 border-zinc-300 dark:border-slate-700 group-hover:border-zinc-400'
                  }`}>
                    {(filters.education || []).includes(level) && (
                      <svg className="w-3 h-3 text-white dark:text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={(filters.education || []).includes(level)}
                    onChange={(e) => {
                      const current = filters.education || [];
                      const updated = e.target.checked
                        ? [...current, level]
                        : current.filter(d => d !== level);
                      updateFilter('education', updated.length > 0 ? updated : undefined);
                    }}
                    className="hidden"
                  />
                  <span className="text-[13px] text-zinc-600 dark:text-slate-400 group-hover:text-zinc-900 dark:group-hover:text-slate-100 transition-colors">
                    {level}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
        )}

        {/* School Filter */}
        {filterVis.school && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.school,
            'school',
            'School',
            expandedFilters.has('school')
          )}

          {expandedFilters.has('school') && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search schools..."
                  value={schoolInput}
                  onChange={(e) => setSchoolInput(e.target.value)}
                  className="w-full h-9 pl-8 pr-3 text-[13px] bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-700 rounded-lg placeholder-zinc-400 dark:placeholder-slate-500 text-zinc-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-slate-400 focus:border-transparent"
                />
              </div>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {filteredSchools.map((school) => (
                  <label key={school} className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                      filters.school === school
                        ? 'bg-zinc-900 dark:bg-slate-100 border-zinc-900 dark:border-slate-100' 
                        : 'bg-white dark:bg-slate-800 border-zinc-300 dark:border-slate-700 group-hover:border-zinc-400'
                    }`}>
                      {filters.school === school && (
                        <svg className="w-3 h-3 text-white dark:text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <input
                      type="radio"
                      name="school"
                      checked={filters.school === school}
                      onChange={(e) => {
                        if (e.target.checked) {
                          updateFilter('school', school);
                        } else {
                          updateFilter('school', undefined);
                        }
                      }}
                      className="hidden"
                    />
                    <span className="text-[13px] text-zinc-600 dark:text-slate-400 group-hover:text-zinc-900 dark:group-hover:text-slate-100 transition-colors">
                      {school}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Certificates Filter */}
        {filterVis.certificates && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.certificates,
            'certificates',
            'Certificates',
            expandedFilters.has('certificates')
          )}

          {expandedFilters.has('certificates') && (
            <div className="space-y-2">
              <div className="max-h-60 overflow-y-auto space-y-2">
                {filterOptions.certs.map((cert) => (
                  <label key={cert} className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                      (filters.certificates || '').toLowerCase().includes(cert.toLowerCase()) 
                        ? 'bg-zinc-900 dark:bg-slate-100 border-zinc-900 dark:border-slate-100' 
                        : 'bg-white dark:bg-slate-800 border-zinc-300 dark:border-slate-700 group-hover:border-zinc-400'
                    }`}>
                      {(filters.certificates || '').toLowerCase().includes(cert.toLowerCase()) && (
                        <svg className="w-3 h-3 text-white dark:text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={(filters.certificates || '').toLowerCase().includes(cert.toLowerCase())}
                      onChange={(e) => {
                        const current = (filters.certificates || '').split(',').map(c => c.trim()).filter(c => c);
                        const updated = e.target.checked
                          ? [...current, cert].join(', ')
                          : current.filter(c => c.toLowerCase() !== cert.toLowerCase()).join(', ');
                        updateFilter('certificates', updated || undefined);
                      }}
                      className="hidden"
                    />
                    <span className="text-[13px] text-zinc-600 dark:text-slate-400 group-hover:text-zinc-900 dark:group-hover:text-slate-100 transition-colors">
                      {cert}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Research Papers Filter */}
        {filterVis.researchPapers && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.researchPapersCount,
            'researchPapersCount',
            'Research Papers',
            expandedFilters.has('researchPapersCount')
          )}

          {expandedFilters.has('researchPapersCount') && (
            <div>
              <input
                type="number"
                placeholder="Minimum count (e.g., 3)"
                min="1"
                value={filters.researchPapersCount || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    updateFilter('researchPapersCount', undefined);
                  } else {
                    const num = parseInt(value);
                    if (!isNaN(num) && num > 0) {
                      updateFilter('researchPapersCount', num);
                    }
                  }
                }}
                className="w-full h-9 px-3 text-[13px] bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-700 rounded-lg placeholder-zinc-400 dark:placeholder-slate-500 text-zinc-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-slate-400 focus:border-transparent"
              />
            </div>
          )}
        </div>
        )}

        {/* Languages - Natural Filter (pills + search, same pattern as Skills) */}
        {filterVis.naturalLanguages && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800">
          {renderFilterHeader(
            filters.naturalLanguages,
            'naturalLanguages',
            'Languages spoken',
            expandedFilters.has('naturalLanguages')
          )}

          {expandedFilters.has('naturalLanguages') && (
            <div className="space-y-2.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search languages…"
                  value={naturalLangInput}
                  onChange={(e) => setNaturalLangInput(e.target.value)}
                  className="w-full h-9 pl-8 pr-3 text-[13px] bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-700 rounded-lg placeholder-zinc-400 dark:placeholder-slate-500 text-zinc-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-slate-400 focus:border-transparent"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {/* Show selected languages not in pills list */}
                {(filters.naturalLanguages || [])
                  .filter((l) => !naturalLangPills.includes(l))
                  .map((lang) => (
                    <button
                      key={`selected-${lang}`}
                      type="button"
                      onClick={() => removeArrayItem('naturalLanguages', lang)}
                      className="h-6 px-2 text-[11.5px] font-semibold rounded-md transition-all bg-zinc-900 dark:bg-slate-100 text-white dark:text-slate-900"
                    >
                      {lang}
                    </button>
                  ))}
                {/* All pills with toggle */}
                {naturalLangPills.map((lang) => {
                  const isSelected = (filters.naturalLanguages || []).includes(lang);
                  return (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          removeArrayItem('naturalLanguages', lang);
                        } else {
                          addArrayItem('naturalLanguages', lang);
                          addToRecentNaturalLangsForDomain(lang, domain);
                          setRecentNaturalLangsDirty(d => d + 1);
                          setNaturalLangInput('');
                        }
                      }}
                      className={`h-6 px-2 text-[11.5px] font-semibold rounded-md transition-all ${
                        isSelected
                          ? 'bg-zinc-900 dark:bg-slate-100 text-white dark:text-slate-900'
                          : 'bg-zinc-100 dark:bg-slate-800 text-zinc-600 dark:text-slate-400 hover:bg-zinc-200 dark:hover:bg-slate-700 hover:text-zinc-900 dark:hover:text-slate-200'
                      }`}
                    >
                      {lang}
                    </button>
                  );
                })}
              </div>
              {naturalLangInput.trim() && naturalLangPills.length === 0 && (
                <p className="text-[11px] text-zinc-500 dark:text-slate-400">
                  No languages matching &quot;{naturalLangInput}&quot;
                </p>
              )}
            </div>
          )}
        </div>
        )}

        {/* Languages - Programming Filter */}
        {filterVis.programmingLanguages && (
        <div className="pb-4 border-b border-zinc-100 dark:border-slate-800 last:border-0 last:pb-0">
          {renderFilterHeader(
            filters.programmingLanguages,
            'programmingLanguages',
            'Languages - Programming',
            expandedFilters.has('programmingLanguages')
          )}

          {expandedFilters.has('programmingLanguages') && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search programming languages..."
                  value={programmingLangInput}
                  onChange={(e) => setProgrammingLangInput(e.target.value)}
                  className="w-full h-9 pl-8 pr-3 text-[13px] bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-700 rounded-lg placeholder-zinc-400 dark:placeholder-slate-500 text-zinc-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-slate-400 focus:border-transparent"
                />
              </div>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {filteredProgrammingLangs.map((lang) => {
                  const isSelected = (filters.programmingLanguages || []).includes(lang);
                  return (
                    <label key={lang} className="flex items-center gap-2 cursor-pointer group">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                        isSelected 
                          ? 'bg-zinc-900 dark:bg-slate-100 border-zinc-900 dark:border-slate-100' 
                          : 'bg-white dark:bg-slate-800 border-zinc-300 dark:border-slate-700 group-hover:border-zinc-400'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white dark:text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addArrayItem('programmingLanguages', lang);
                          } else {
                            removeArrayItem('programmingLanguages', lang);
                          }
                        }}
                        className="hidden"
                      />
                      <span className="text-[13px] text-zinc-600 dark:text-slate-400 group-hover:text-zinc-900 dark:group-hover:text-slate-100 transition-colors">
                        {lang}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}

      </div>
    </div>
  );
};

export default FilterSection;
